/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import AddParticipantModal from '@brands/ChatWindow/ChatModal/AddParticipantModal';
import DownloadModal from '@brands/ChatWindow/ChatModal/DownloadModal';
import LeaveModal from '@brands/ChatWindow/ChatModal/LeaveModal';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import FileUploadProgressBar from '@brands/Components/UploadProgressBar/FileUploadProgressBar';
import { useConversationMessages, useIdentityContext } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { IConversationStatistics } from '@brands/services/communication/listConversationStatistics';
import { markLastReadMessage } from '@brands/services/communication/markLastReadMessage';
import { IGMConversation } from '@brands/services/communication/types/IGMConversation';
import { IGMConversationParticipant } from '@brands/services/communication/types/IGMConversationParticipant';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectShowVideoThumbnail } from '@brands/store/selectors/showVideoThumbnail';
import { requestStatisticsRefresh } from '@brands/store/slices/conversationStatisticsSlice';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Button } from '@mui/material';
import { Conversation } from '@twilio/conversations';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { IoIosSearch } from 'react-icons/io';
import { IoDownloadOutline } from 'react-icons/io5';
import { MdKeyboardArrowLeft, MdOutlinePersonAddAlt } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CareAdvisorProfileCardModal from '../../../Dashboard/ProfileCards/CareAdvisor/ProfileCards';
import PatientProfileCardModal from '../../../Dashboard/ProfileCards/Patient/ProfileCards';
import ProviderProfileCardModal from '../../../Dashboard/ProfileCards/Provider/ProfileCards';
import CaseActiveChatBubble from '../../ChatModal/ChatBubble/PatientCaseActiveChatBubble';
import styles from './case-styles.module.scss';
import CaseChatFooter from './CaseChatFooter';
import { getFriendlyRoleName } from '@brands/Utils/getFriendlyName';
import { shouldShowDoctorTitle } from '@brands/Utils/getProviderTitle';
import CollectorProfileCardModal from '@brands/Dashboard/ProfileCards/Collector/ProfileCards';

interface MessageBodyProps {
  twilioConversation: Conversation | null;
  readOnlyConversation: IGMConversation | null;
  isMobile: boolean;
  isProfileCard?: boolean;
  isVideoConversation?: boolean;
  setLeaveModalOpen: (isOpen: boolean) => void;
  leaveModalOpen: boolean;
  conversationSid: string | null;
  setConversationSid: (sid: string | null) => void;
  currentCaseId: number | undefined;
  updateUnreadNotification: () => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  conversations: IConversationStatistics[];
  selectedConversationParticipants: IGMConversationParticipant[];
  selectedConversationForMessageBody: IGMConversation | undefined;
  setSelectedConversationParticipants: React.Dispatch<React.SetStateAction<IGMConversationParticipant[]>>;
  setSelectedConversationForMessageBody: React.Dispatch<React.SetStateAction<IGMConversation | undefined>>;
  refresh: () => Promise<void>;
}

const CaseMessageBody: React.FC<MessageBodyProps> = ({
  twilioConversation,
  readOnlyConversation,
  isMobile,
  isProfileCard,
  isVideoConversation,
  leaveModalOpen,
  setLeaveModalOpen,
  conversationSid,
  setConversationSid,
  currentCaseId,
  updateUnreadNotification,
  loading,
  setLoading,
  conversations,
  selectedConversationParticipants,
  selectedConversationForMessageBody,
  setSelectedConversationParticipants,
  setSelectedConversationForMessageBody,
  refresh,
}) => {
  const contextValues = useIdentityContext();
  const roleNavigate = useRoleNavigate();
  const dispatch = useDispatch();
  const routeConversationSid = useParams().conversationSid || null;
  const { userInfo } = useAppSelector(selectAuth);
  const identity = contextValues.userInfo?.id.toString();
  const [thisCase, setCase] = useState<ICase>();
  // The "patient" participant slot on a case conversation can be keyed to
  // either the dependent's own id or their guardian's id depending on how the
  // conversation was created (see CaseMessageInfoCreateNewConversation, which
  // uses guardian_id for a Dependent patient). Whichever one of the pair is
  // logged in, accept a match on either id so neither side goes read-only.
  const myEffectiveIdentities =
    thisCase?.patient?.guardian_id != null &&
    (Number(thisCase.patient.guardian_id) === contextValues.userInfo?.id ||
      Number(thisCase.patient.id) === contextValues.userInfo?.id)
      ? [Number(thisCase.patient.id), Number(thisCase.patient.guardian_id)]
      : [contextValues.userInfo?.id];
  const [patientCurrentState, setPatientCurrentState] = useState('');
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [profileCardModal, setProfileCardModal] = useState(false);
  const [providerProfileCardModal, setProviderProfileCardModal] = useState(false);
  const [caProfileCardModal, setCAProfileCardModal] = useState(false);
  const [collectorProfileCardModal, setCollectorProfileCardModal] = useState(false);
  const [participant, setParticipant] = useState<UserProfile | null>(null);
  const [search, setSearch] = useState<string>('');
  const [query, setQuery] = useState<string>('');
  const messagesParams = useMemo(() => (search ? { search } : {}), [search]);
  const [progress, setProgress] = useState(0);
  const conversation = conversations.find(
    (selectedConversation) => selectedConversation.conversation_sid === conversationSid,
  ) as IConversationStatistics;
  const [displayProgressBar, setDisplayProgressBar] = useState(false);
  const [choosenFiles, setChoosenFiles] = useState<File[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [isInEditMode, setIsInEditMode] = useState<string>('');
  const showVideoThumbnail = useSelector(selectShowVideoThumbnail);
  const { messages, editMessage, deleteMessage, sendMessage, loadNextPage } = useConversationMessages(
    twilioConversation,
    messagesParams,
    readOnlyConversation,
  );
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [addParticipantModalOpen, setAddParticipantModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const ref = useDetectClickOutside({ onTriggered: () => setShowDropDown(false) });
  const userRole = useMemo(() => {
    if (!contextValues.userInfo) {
      return null;
    }

    return contextValues.userInfo.role;
  }, [contextValues]);

  const listInnerRef = useRef<HTMLDivElement>(null);

  const throttledSearch = throttle(setSearch, 100, { trailing: true });

  const [loadedNextPage, setLoadedNextPage] = useState(false);

  const unreadMessageIndicator = useMemo(() => {
    return conversation?.unread_message_count > 0 ? conversation.unread_message_count : 0;
  }, [conversation]);

  const unreadCount = unreadMessageIndicator;

  const getParticipantName = (id: string): string => {
    return (
      (selectedConversationParticipants &&
        selectedConversationParticipants.find((p) => Number(p.identity) === Number(id))?.full_name) ||
      id
    );
  };

  useEffect(() => {
    const fetchCase = async (): Promise<void> => {
      const currentRelatedCase = await getOneCase(currentCaseId || 0);
      if (currentRelatedCase) {
        setPatientCurrentState(currentRelatedCase.patient_current_state || '');
        setCase(currentRelatedCase);
      }
    };

    fetchCase();
  }, [readOnlyConversation]);

  const displayParticipantProfile = (
    e: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>,
    roleName: string,
  ): void => {
    e.stopPropagation();
    e.preventDefault();
    if (roleName === UserRoleName.Patient || roleName === UserRoleName.Dependent) {
      if (userInfo.role.name === UserRoleName.Provider) {
        const targetPatient = thisCase?.patient;
        if (targetPatient?.full_name) {
          const queryString = new URLSearchParams({
            patient_full_name: targetPatient.full_name,
          }).toString();
          roleNavigate(`/dashboard?${queryString}`);
          return;
        }
      }
      setParticipant(thisCase?.patient || null);
      setProfileCardModal(true);
    } else if (roleName === UserRoleName.Provider) {
      setParticipant(thisCase?.provider || null);
      setProviderProfileCardModal(true);
    } else if (roleName === UserRoleName.CareAdvisor) {
      setParticipant(thisCase?.care_advisor || null);
      setCAProfileCardModal(true);
    } else if (roleName === UserRoleName.Collector) {
      setParticipant(thisCase?.collector || null);
      setCollectorProfileCardModal(true);
    }
  };

  const onScroll = useCallback(() => {
    if (!listInnerRef.current) {
      return;
    }
    const { scrollTop } = listInnerRef.current;
    if (scrollTop === 0) {
      setLoadedNextPage(true);
      loadNextPage();
    }
  }, [loadNextPage]);

  useEffect(() => {
    if (isMobile) {
      setEditMode(isInEditMode !== '');
    }
  }, [isInEditMode, setEditMode]);

  const handleNavigateBack = (): void => {
    const currentPath = window.location.pathname;
    const segments = currentPath.split('/');
    const newPath = segments.slice(0, segments.length - 1).join('/');
    roleNavigate(newPath);
    setConversationSid(null);
  };

  const addParticipant = (): void => {
    setAddParticipantModalOpen(true);
  };

  const scrollDown = (): void => {
    if (listInnerRef.current) {
      const lastLi = listInnerRef.current.lastElementChild;
      if (lastLi) {
        const { scrollHeight } = listInnerRef.current;
        const { offsetHeight } = listInnerRef.current;
        const lastLiHeight = lastLi.clientHeight;
        listInnerRef.current.scrollTop = scrollHeight - offsetHeight + lastLiHeight;
      }
    }
  };

  const scrollTothis = (number: number, index: number): void => {
    if (index === messages.length - 1 && listInnerRef.current) {
      listInnerRef.current.scrollTop = number;
    }
  };

  const handleMediaLoad = (index: number): void => {
    if ((index === messages.length - 1 || index >= messages.length - 10) && messages[index].media_ids.length > 0) {
      scrollDown();
    }
  };
  useEffect(() => {
    if (!loadedNextPage) {
      scrollDown();
    }
    setLoadedNextPage(false);
  }, [messages, listInnerRef]);

  useEffect(() => {
    scrollDown();
    if (!isMobile || selectedConversationParticipants.length > 0 || selectedConversationForMessageBody !== undefined) {
      if (selectedConversationParticipants || selectedConversationForMessageBody) {
        setSelectedConversationParticipants(selectedConversationParticipants);
        setSelectedConversationForMessageBody(selectedConversationForMessageBody);
      }
    } else {
      // Mirror CaseMessageInfo: the header lists active participants. A
      // reassigned CA lingers as an `inactive` participant and would
      // otherwise show greyed out in place of the current CA. Fall back to
      // all remotes when none are active (e.g. an archived thread or a
      // participant who left) so the header never renders empty. Message
      // bubbles keep the full participant list, so previous CAs stay
      // attributable when scrolling the thread. A dependent's own row is
      // left out too, as in CaseMessageInfo, so a guardian doesn't see their
      // dependent listed as a second party.
      const isRemote = (p: IGMConversationParticipant): boolean =>
        Number(p.identity) !== Number(userInfo.id) && p.role !== UserRoleName.Dependent;
      const activeRemote =
        readOnlyConversation?.participants?.filter((p) => isRemote(p) && p.status === 'active') ?? [];
      const remote = (
        activeRemote.length > 0 ? activeRemote : readOnlyConversation?.participants?.filter(isRemote) ?? []
      ) as IGMConversationParticipant[];
      if (conversation && conversation?.conversation_sid === routeConversationSid && remote && readOnlyConversation) {
        setSelectedConversationParticipants(remote as IGMConversationParticipant[]);
        setSelectedConversationForMessageBody(readOnlyConversation);
      }
    }
  }, []);

  const markAsRead = async (): Promise<void> => {
    const lastMessageSid = messages[messages.length - 1]?.sid;

    if (conversationSid && conversation?.conversation_sid && lastMessageSid && unreadCount > 0) {
      try {
        await markLastReadMessage(conversationSid, lastMessageSid, userInfo?.role?.name);
        updateUnreadNotification();

        dispatch(requestStatisticsRefresh());
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  useEffect(() => {
    if (conversationSid && messages.length > 0) {
      void markAsRead();
    }
  }, [conversationSid, messages[messages.length - 1]?.sid, unreadCount]);

  return (
    <>
      <div
        className={styles.chatBody}
        style={{
          height: isVideoConversation ? 'calc(100% - 50px)' : '',
          width: isVideoConversation || isProfileCard ? '100%' : '',
          margin: isVideoConversation ? '0' : '',
          display: conversationSid || conversation?.conversation_sid ? 'flex' : 'none',
        }}
      >
        <div
          className={`${styles.messageSearch} ${showVideoThumbnail && styles.addMarginTop}`}
          style={{ display: isProfileCard ? 'none' : 'flex' }}
        >
          {isMobile && (
            <Button
              type="button"
              variant="text"
              className={styles.backButton}
              sx={{
                width: 'fit-content',
              }}
              onClick={() => {
                handleNavigateBack();
              }}
            >
              <MdKeyboardArrowLeft />
              Back
            </Button>
          )}
          <div className={styles.wrapDiv}>
            {selectedConversationParticipants?.map((remoteParticipantSingle) => (
              <div
                className={`${styles.participantDivInfo} ${
                  remoteParticipantSingle.status.toLowerCase() !== 'active' ? styles.inactiveParticipant : ''
                }`}
                onClick={(e) => displayParticipantProfile(e, remoteParticipantSingle.role)}
              >
                <span className={`${styles.profileImageDate} ${styles.imageContainer}`}>
                  {unreadCount > 0 && <span className={styles.unreasMessage} />}

                  <img
                    alt="Avatar"
                    onError={(event: any) => {
                      event.target.src =
                        'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                      event.target.onError = null;
                    }}
                    className={`fs.exclude ${styles.profileImage}`}
                    src={getUserAvatar(Number(remoteParticipantSingle?.identity))}
                  />
                </span>
                <div className={`${styles.details} ${isMobile && styles.mobileCaseMessageInfo}`}>
                  <div className={styles.detailsNameImage}>
                    <span className={`${styles.participantName} fs.exclude ${!false ? styles.primaryColor : ''}`}>
                      {remoteParticipantSingle.role === 'Provider' &&
                        shouldShowDoctorTitle(thisCase?.provider?.provider_profile?.credentials) && (
                          <span style={{ paddingRight: '4px' }}>Dr.</span>
                        )}
                      {remoteParticipantSingle.full_name}
                    </span>
                  </div>
                  <span className={`fs-unmask ${styles.conversationType}`}>
                    {getFriendlyRoleName(remoteParticipantSingle.role)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.searchBoxDiv}>
            <div className={styles.searchBox}>
              <input
                type="text"
                className={`fs.exclude ${styles.inputSearch}`}
                placeholder="Search in this chat"
                onChange={(e) => {
                  setQuery(e.target.value);
                  throttledSearch(e.target.value);
                }}
                value={query}
              />
              <button aria-label="Search" type="button" className={styles.btnSearch} onClick={() => setSearch(query)}>
                <IoIosSearch className={styles.searchIcon} />
              </button>
            </div>
            <div ref={ref} style={{ position: 'relative' }} className={styles.bsThreeDotsVertical}>
              <BsThreeDotsVertical
                id="options"
                className={styles.options}
                onClick={(): void => setShowDropDown(!showDropDown)}
              />
              {showDropDown && (
                <div className={styles.dropDown}>
                  {false &&
                    (userRole?.name === UserRoleName.Provider || userRole?.name === UserRoleName.CareAdvisor) && (
                      <div onClick={() => addParticipant()} className={styles.borderBottom}>
                        Add Participant
                        <MdOutlinePersonAddAlt className={styles.dropDownIcon} />
                      </div>
                    )}
                  {thisCase?.status === 'completed' && thisCase?.one_report?.id && thisCase?.report_available && (
                    <div onClick={() => roleNavigate(`/oral-health-report/${thisCase?.id}`)}>
                      View Report
                      <DescriptionOutlinedIcon className={styles.dropDownIcon} />
                    </div>
                  )}
                  <div onClick={() => setDownloadModalOpen(true)}>
                    Download Chat
                    <IoDownloadOutline className={styles.dropDownIcon} />
                  </div>
                </div>
              )}
            </div>
          </div>
          {leaveModalOpen && (
            <LeaveModal setOpenModal={setLeaveModalOpen} conversationSid={conversation?.conversation_sid} />
          )}
          {downloadModalOpen && (
            <DownloadModal
              setOpenModal={setDownloadModalOpen}
              conversationSid={conversation?.conversation_sid}
              setLoading={setLoading}
            />
          )}
        </div>
        {loading ? (
          <Loading dotted />
        ) : (
          <>
            {displayProgressBar && (
              <div className={styles.progressBar}>
                {displayProgressBar && <FileUploadProgressBar progress={progress} />}
              </div>
            )}
            <div
              className={`${styles.popupMessageContainer} w-100 
              ${showVideoThumbnail ? styles.adjustHeight : styles.keepCurrentHeight}
              ${isMobile && editMode && styles.forceEditPopupMessageContainer}`}
              onScroll={onScroll}
              ref={listInnerRef}
            >
              <ul className="h-100">
                {messages
                  .sort((a, b) => a.index - b.index)
                  .map((message, index) => (
                    <CaseActiveChatBubble
                      messageCreator={selectedConversationForMessageBody?.participants?.find(
                        (par) => Number(par.identity) === Number(message.created_by),
                      )}
                      messageIndex={index}
                      handleMediaLoad={handleMediaLoad}
                      conversationState={selectedConversationForMessageBody?.state}
                      direction={Number(message.created_by) === Number(identity) ? 'outgoing' : 'incoming'}
                      messageDetails={{
                        id: message.sid,
                      }}
                      participants={selectedConversationForMessageBody?.participants}
                      providerCredentials={thisCase?.provider?.provider_profile?.credentials}
                      message={message}
                      editMessage={editMessage}
                      deleteMessage={deleteMessage}
                      isVCRoom={isVideoConversation}
                      listInnerRef={listInnerRef}
                      scrollTothis={scrollTothis}
                      setEditMode={setEditMode}
                      isInEditMode={isInEditMode}
                      setIsInEditMode={setIsInEditMode}
                      currentCaseId={currentCaseId}
                      isLastMessage={index === messages.length - 1}
                      hoveredMessageId={hoveredMessageId}
                      setHoveredMessageId={setHoveredMessageId}
                    />
                  ))}
                <li />
              </ul>
            </div>

            {!editMode &&
            (selectedConversationForMessageBody?.state === 'active' ||
              selectedConversationForMessageBody?.state === 'inactive') &&
            selectedConversationForMessageBody?.participants?.find((me) =>
              myEffectiveIdentities.includes(Number(me.identity)),
            )?.status === 'active' ? (
              <div className={styles.chatFooter}>
                <CaseChatFooter
                  conversationSid={conversation?.conversation_sid}
                  sendMessage={sendMessage}
                  choosenFiles={choosenFiles}
                  setChoosenFiles={setChoosenFiles}
                  setDisplayProgressBar={setDisplayProgressBar}
                  displayProgressBar={displayProgressBar}
                  setProgress={setProgress}
                  conversationType={selectedConversationForMessageBody?.friendly_name || ''}
                  caseType={conversation?.case_type}
                  twilioConversation={twilioConversation}
                  getParticipantName={getParticipantName}
                />
              </div>
            ) : (
              !editMode &&
              selectedConversationForMessageBody?.state === 'closed' && (
                <div className={`${styles.chatFooterInActive} ${styles.messagesFooter} fs-exclude`}>
                  <span>This conversation has been archived</span>
                </div>
              )
            )}
          </>
        )}
      </div>
      {addParticipantModalOpen && (
        <AddParticipantModal
          setOpenModal={setAddParticipantModalOpen}
          conversationSid={conversation?.conversation_sid ?? ''}
        />
      )}
      {profileCardModal && (
        <PatientProfileCardModal
          patientIdentity={participant}
          isOpen={profileCardModal}
          toggleModal={setProfileCardModal}
        />
      )}
      {providerProfileCardModal && (
        <ProviderProfileCardModal
          isOpen={providerProfileCardModal}
          toggleModal={setProviderProfileCardModal}
          providerIdentity={participant}
          patientCurrentStateValue={patientCurrentState}
        />
      )}
      {caProfileCardModal && (
        <CareAdvisorProfileCardModal
          isOpen={caProfileCardModal}
          toggleModal={setCAProfileCardModal}
          careAdvisorIdentity={participant}
        />
      )}
      {collectorProfileCardModal && (
        <CollectorProfileCardModal
          isOpen={collectorProfileCardModal}
          toggleModal={setCollectorProfileCardModal}
          collectorIdentity={participant}
        />
      )}
    </>
  );
};

CaseMessageBody.defaultProps = {
  isVideoConversation: false,
  isProfileCard: false,
};

export default CaseMessageBody;
